import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone)) newErrors.phone = 'Enter a valid 10-digit mobile number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919821767267?text=Hi, I would like to enquire about your furniture.', '_blank');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-12 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Get in Touch</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] tracking-tight">
            Contact Us
          </h1>
          <p className="font-body text-[#666666] mt-4 max-w-xl">
            Have questions about our furniture or custom orders? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="font-body text-sm">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 rounded-none ${errors.name ? 'border-red-500' : ''}`}
                    data-testid="contact-input-name"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="font-body text-sm">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="9876543210"
                      className={`mt-1 rounded-none ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="contact-input-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-body text-sm">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`mt-1 rounded-none ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="contact-input-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="message" className="font-body text-sm">Your Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className={`mt-1 rounded-none resize-none ${errors.message ? 'border-red-500' : ''}`}
                    placeholder="Tell us about your requirements..."
                    data-testid="contact-input-message"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary flex items-center justify-center disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-6">Contact Information</h2>
              
              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C4A882]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C4A882]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Email</h3>
                    <a href="mailto:usmanfurniturewala@gmail.com" className="font-body text-[#666666] hover:text-[#C4A882] transition-colors">
                      usmanfurniturewala@gmail.com
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">WhatsApp</h3>
                    <button 
                      onClick={handleWhatsApp}
                      className="font-body text-[#666666] hover:text-[#25D366] transition-colors"
                      data-testid="whatsapp-link"
                    >
                      Chat with us on WhatsApp
                    </button>
                  </div>
                </div>

            

                {/* Workshop Address */}
                <div className="flex items-start" data-testid="contact-workshop-address">
                  <div className="w-12 h-12 bg-[#C4A882]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#C4A882]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Workshop</h3>
                    <address className="font-body text-[#666666] not-italic leading-relaxed">
                      95, Nizam Street,<br />
                      Mumbai - 400003
                    </address>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-8 bg-[#F9F9F8] h-64 flex items-center justify-center" data-testid="map-placeholder">
                <div className="text-center">
                  <MapPin className="w-8 h-8 mx-auto mb-2 text-[#C4A882]" />
                  <p className="font-body text-sm text-[#666666]">Nizam Street, Mumbai</p>
                  <a 
                    href="https://maps.google.com/?q=95+Nizam+Street,+Mumbai+400003"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm text-[#C4A882] hover:underline mt-2 inline-block"
                  >
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
